import Phaser from "phaser";
import type { PlayerPos } from "@/net/types";
import { tileFoot, worldDirToFrame, type ScreenPos } from "../map/Iso";
import { AVATAR_ATLAS_KEY, atlasKeyFor } from "./AvatarAtlas";
import type { AvatarType } from "@/ui/PlayerCustomization";

const TINTS = [0xa8c3f0, 0xf0a89a, 0x9ae0b8, 0xf0d88a, 0xc89af0, 0x9af0ea];

const EMOTE_ICONS: Record<string, string> = {
  wave: "👋",
  laugh: "😂",
  heart: "❤️",
  cry: "😢",
  angry: "😠",
  thumbsup: "👍",
  fire: "🔥",
  star: "⭐",
  think: "🤔",
  clap: "👏",
};

function tintFor(userId: string): number {
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    hash = (hash * 31 + userId.charCodeAt(i)) | 0;
  }
  return TINTS[Math.abs(hash) % TINTS.length] ?? 0xffffff;
}

export class PlayerSprite {
  readonly userId: string;
  readonly container: Phaser.GameObjects.Container;
  private readonly origin: ScreenPos;
  private body: Phaser.GameObjects.Sprite;
  private label: Phaser.GameObjects.Text;
  private chatBubble: Phaser.GameObjects.Text | null = null;
  private chatBubbleTween: Phaser.Tweens.Tween | null = null;

  private readonly isSelf: boolean;
  private targetWorldX: number;
  private targetWorldY: number;
  private currentWorldX: number;
  private currentWorldY: number;
  private currentDir: number;

  constructor(
    scene: Phaser.Scene,
    userId: string,
    pos: PlayerPos,
    isSelf: boolean,
    origin: ScreenPos,
    displayName?: string,
  ) {
    this.userId = userId;
    this.isSelf = isSelf;
    this.origin = origin;
    this.targetWorldX = pos.x;
    this.targetWorldY = pos.y;
    this.currentWorldX = pos.x;
    this.currentWorldY = pos.y;
    this.currentDir = pos.dir;

    const { sx, sy } = tileFoot(pos.x, pos.y);

    this.body = scene.add.sprite(0, 0, AVATAR_ATLAS_KEY, `d${worldDirToFrame(pos.dir)}`);
    this.body.setOrigin(0.5, 1);
    this.body.setScale(0.8);
    this.body.setTint(tintFor(userId));

    this.label = scene.add.text(0, -42, displayName || userId.slice(0, 6), {
      fontSize: "10px",
      color: isSelf ? "#ffffff" : "#cccccc",
      backgroundColor: "rgba(0,0,0,0.35)",
      padding: { left: 3, right: 3, top: 1, bottom: 1 },
    });
    this.label.setOrigin(0.5, 0.5);

    this.container = scene.add.container(origin.sx + sx, origin.sy + sy, [this.body, this.label]);
    this.container.setDepth(this.computeDepth(pos.x, pos.y));

    if (isSelf) {
      const ring = scene.add.ellipse(0, 0, 18, 8, 0xffffff, 0.4);
      ring.setStrokeStyle(1, 0xffffff, 0.8);
      this.container.add(ring);
      this.container.sendToBack(ring);
    }
  }

  setTarget(pos: PlayerPos): void {
    this.targetWorldX = pos.x;
    this.targetWorldY = pos.y;
    if (pos.dir !== this.currentDir) {
      this.currentDir = pos.dir;
      this.body.setFrame(`d${worldDirToFrame(pos.dir)}`);
    }
  }

  getTilePos(): { x: number; y: number } {
    return { x: this.currentWorldX, y: this.currentWorldY };
  }

  tick(dtMs: number): void {
    if (this.isSelf) {
      this.currentWorldX = this.targetWorldX;
      this.currentWorldY = this.targetWorldY;
    } else {
      // Frame-rate independent lerp: same convergence speed regardless of FPS
      const alpha = 1 - Math.pow(0.75, dtMs / 16.667);
      this.currentWorldX += (this.targetWorldX - this.currentWorldX) * alpha;
      this.currentWorldY += (this.targetWorldY - this.currentWorldY) * alpha;
    }

    const { sx, sy } = tileFoot(this.currentWorldX, this.currentWorldY);
    this.container.x = this.origin.sx + sx;
    this.container.y = this.origin.sy + sy;
    this.container.setDepth(this.computeDepth(this.currentWorldX, this.currentWorldY));

    if (this.chatBubble) {
      this.chatBubble.x = this.container.x;
      this.chatBubble.y = this.container.y - 52;
    }
  }

  showChat(scene: Phaser.Scene, text: string): void {
    this.chatBubbleTween?.destroy();
    this.chatBubble?.destroy();

    this.chatBubble = scene.add
      .text(this.container.x, this.container.y - 52, text.slice(0, 80), {
        fontSize: "12px",
        color: "#ffffff",
        backgroundColor: "#1a1d24",
        padding: { left: 6, right: 6, top: 3, bottom: 3 },
        wordWrap: { width: 200, useAdvancedWrap: true },
      })
      .setOrigin(0.5, 1)
      .setDepth(100000);

    this.chatBubbleTween = scene.tweens.add({
      targets: this.chatBubble,
      alpha: 0,
      delay: 3000,
      duration: 1000,
      ease: "Quad.easeIn",
      onComplete: () => {
        this.chatBubble?.destroy();
        this.chatBubble = null;
        this.chatBubbleTween = null;
      },
    });
  }

  destroy(): void {
    this.chatBubbleTween?.destroy();
    this.chatBubble?.destroy();
    this.container.destroy();
  }

  showEmote(scene: Phaser.Scene, emoteId: string): void {
    const icon = EMOTE_ICONS[emoteId] ?? emoteId;
    const emote = scene.add.text(this.container.x, this.container.y - 52, icon, {
      fontSize: "24px",
    })
      .setOrigin(0.5, 1)
      .setDepth(100001)
      .setScale(0.2)
      .setAlpha(1);

    scene.tweens.add({
      targets: emote,
      scaleX: 1.2,
      scaleY: 1.2,
      y: emote.y - 30,
      duration: 400,
      ease: "Back.easeOut",
      onComplete: () => {
        scene.tweens.add({
          targets: emote,
          scaleX: 1,
          scaleY: 1,
          y: emote.y - 20,
          alpha: 0,
          duration: 800,
          ease: "Quad.easeIn",
          onComplete: () => emote.destroy(),
        });
      },
    });
  }

  setColor(tint: number): void {
    this.body.setTint(tint);
  }

  setLabel(name: string): void {
    this.label.setText(name || this.userId.slice(0, 6));
  }

  setAvatarType(type: AvatarType): void {
    const key = atlasKeyFor(type);
    this.body.setTexture(key, `d${worldDirToFrame(this.currentDir)}`);
  }

  private computeDepth(worldX: number, worldY: number): number {
    return (worldX + worldY) * 1000;
  }
}
